import { ReactNode, useEffect, useLayoutEffect, useRef } from "react";

import { Button } from "@smart/itops-sb-design-system-dom";
import { detectOSAndBrowser } from "@smart/itops-utils-basic";

import { GroupContainer, GroupFieldSetContainer } from "./group-components";
import { GroupItem } from "../types";

const findScrollableParent = (element: HTMLElement) => {
  if (!element) return undefined;

  const hasScrollableContent = (el: HTMLElement) => {
    const style = window.getComputedStyle(el);
    return (
      el.scrollHeight > el.clientHeight &&
      (style.overflowY === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "overlay")
    );
  };

  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (hasScrollableContent(parent)) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return document.documentElement;
};

export type IntakeGroupProps = {
  group: GroupItem;
  onAdd?: () => void;
  children?: ReactNode;
  scrollPositionRef?: React.RefObject<{
    scrollContainer: HTMLElement | undefined;
    scrollPosition: number | undefined;
  }>;
};

export type IntakeGroupSetProps = {
  group: GroupItem;
  index: number;
  onRemove?: () => void;
  children?: ReactNode;
};

export const IntakeGroup = ({
  group,
  children,
  onAdd,
  scrollPositionRef,
}: IntakeGroupProps) => {
  /**
   * Safari does not maintain the scroll position when the component is remounted.
   * We have to manually store the scroll position and restore it when the component is remounted.
   */
  const groupContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(
    () => () => {
      if (
        scrollPositionRef?.current &&
        scrollPositionRef?.current?.scrollContainer
      ) {
        scrollPositionRef.current.scrollPosition =
          scrollPositionRef.current.scrollContainer.scrollTop;
      }
    },
    [],
  );

  useEffect(() => {
    const { browser } = detectOSAndBrowser(navigator.userAgent);
    const scrollContainer = scrollPositionRef?.current?.scrollContainer;
    const scrollPosition = scrollPositionRef?.current?.scrollPosition;

    if (
      browser === "Safari" &&
      scrollPositionRef?.current &&
      !scrollContainer &&
      groupContainerRef.current
    ) {
      scrollPositionRef.current.scrollContainer = findScrollableParent(
        groupContainerRef.current,
      );
    }

    if (scrollPosition && scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: "instant",
      });
      scrollPositionRef.current.scrollPosition = undefined;
    }
  }, []);

  return (
    <GroupContainer group={group} ref={groupContainerRef}>
      {children}
      {onAdd && (
        <div className="mt-[-0.8rem]">
          <Button
            leftIcon="regularPlus"
            variant="primarySubtle"
            onClick={onAdd}
            text={group.repeatPrompt || "Add"}
            dataTestId="add-button"
          />
        </div>
      )}
    </GroupContainer>
  );
};

export const IntakeGroupFieldSet = ({
  group,
  index,
  onRemove,
  children,
}: IntakeGroupSetProps) => (
  <GroupFieldSetContainer group={group} index={index}>
    {children}
    {onRemove && (
      <div className="flex items-start">
        <Button
          variant="secondarySubtle"
          leftIcon="regularTrashCan"
          text="Delete"
          onClick={onRemove}
          className="text-red-550"
          dataTestId="remove-button"
        />
      </div>
    )}
  </GroupFieldSetContainer>
);
